import dayjs from "dayjs";
import { Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import { THEME } from "../overview/boardStyles";
import type { CoordinatorFilterValue } from "../_components/dashboardCoordinators";
import { coordinatorDisplayName } from "../_components/dashboardCoordinators";
import { FINANCE_STATUS_OPTIONS, FinanceControls } from "./financialDashboard.utils";

type ControlsProps = { value: FinanceControls; onChange: (next: FinanceControls) => void };

/** Finance-specific controls inside the existing dashboard funnel. */
export function FinanceToolbarFilters({ value, onChange }: ControlsProps) {
  return <Box sx={{ px: 2, py: 1 }}>
    <TextField select fullWidth size="small" label="Invoice sending" value={value.status}
      onChange={event => onChange({ ...value, status: event.target.value as FinanceControls["status"] })}
      sx={{ mt: 1, "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiSvgIcon-root": { color: THEME.white },
        "& fieldset": { borderColor: THEME.pink } }}>
      <MenuItem value="all">All invoice statuses</MenuItem>
      {Object.entries(FINANCE_STATUS_OPTIONS).map(([key, option]) => <MenuItem key={key} value={key}>{option.label}</MenuItem>)}
    </TextField>
    <FormControlLabel sx={{ mt: 1, ml: 0 }} control={<Switch checked={value.archivedOnly}
      onChange={event => onChange({ ...value, archivedOnly: event.target.checked })}
      inputProps={{ "aria-label": "Archived shows only" }} color="secondary" />} label="Archived shows only" />
    <Typography variant="body2" sx={{ color: THEME.muted, fontSize: 13 }}>Ended, approved shows in the selected date range.</Typography>
  </Box>;
}

type SummaryProps = {
  value: FinanceControls; search: string; coordinators: CoordinatorFilterValue;
  showDuplicates: boolean; unsaved: boolean; onClear: () => void;
};

/** The information button describes the filters used by both the report and CSV. */
export function FinanceFilterSummary({ value, search, coordinators, showDuplicates, unsaved, onClear }: SummaryProps) {
  const details = [
    ["Show end dates", `${value.from ? dayjs(value.from).format("MM/DD/YYYY") : "Not set"} – ${value.to ? dayjs(value.to).format("MM/DD/YYYY") : "Not set"}`],
    ["Invoice sending", value.status === "all" ? "All invoice statuses" : FINANCE_STATUS_OPTIONS[value.status].label],
    ["Shows", value.archivedOnly ? "Archived shows only" : "Active and archived shows"],
    ["Coordinators", coordinators.selected.length
      ? `${coordinators.match === "all" ? "All" : "Any"}: ${coordinators.selected.map(coordinatorDisplayName).join(", ")}` : "All within your access"],
    ["Search", search || "None"],
    ["Duplicates", showDuplicates ? "Included and marked" : "Hidden"],
    ["Order / pages", "Show end date, newest first · 25 shows per page"],
  ];
  return <Box sx={{ p: 1, maxWidth: 480 }}>
    <Typography sx={{ fontWeight: 600, mb: 1 }}>Currently Applied Filters</Typography>
    {unsaved && <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>You have unsaved filter changes</Typography>}
    <Stack gap={1}>{details.map(([label, text]) => <Box key={label}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2">{text}</Typography>
    </Box>)}</Stack>
    <Typography variant="body2" sx={{ mt: 1 }}>Save remembers filter preferences; dates open on the current month and can be changed by day.</Typography>
    <Button variant="text" size="small" onClick={onClear} sx={{ mt: 1 }}>Clear all filters</Button>
  </Box>;
}
